import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { useActiveWeb3React } from 'hooks'
import { MERKL_DISTRIBUTOR_ADDRESS } from '../../constants'
import { useContract } from 'hooks/useContract'
import Distributor from 'abis/merkl/distributor.json'
import { Contract } from '@ethersproject/contracts'
interface Creator {
  address: string
  tags?: string[]
  creatorId?: string | null
}

interface Explorer {
  id: string
  type: string
  url: string
  chainId: number
}

interface Chain {
  id: number
  name: string
  icon: string
  Explorer: Explorer[]
}

interface Token {
  address: string
  chainId: number
  symbol: string
  decimals: number
}

interface Breakdown {
  reason: string
  amount: string
  claimed: string
  pending: string
  campaignId: string
}

interface RewardItem {
  root: string
  recipient: string
  amount: string
  claimed: string
  pending: string
  proofs: string[]
  token: Token
  breakdowns: Breakdown[]
}

interface RewardData {
  chain: Chain
  rewards: RewardItem[]
}

interface RewardToken {
  id: string
  name: string | null
  chainId: number
  address: string
  decimals: number
  icon: string
  verified: boolean
  isTest: boolean
  isPoint: boolean
  isPreTGE: boolean
  price: number | null
  symbol: string
}

interface CampaignStatus {
  campaignId: string
  computedUntil: string | number
  processingStarted: string | number
  status: string
  error?: string
  details?: any
}

interface Campaign {
  id: string
  computeChainId: number
  distributionChainId: number
  campaignId: string
  type: string
  subType: number | null
  rewardTokenId: string
  amount: string
  opportunityId: string
  startTimestamp: string | number
  endTimestamp: string | number
  creatorAddress: string
  creator: Creator
  params: any
  description: string
  chain: Chain
  rewardToken: RewardToken
  distributionChain: Chain
  campaignStatus: CampaignStatus
  createdAt: string
  rootCampaignId: string
  parentCampaignId: string
  opportunity?: any
}

export function useMerklDistributor(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? MERKL_DISTRIBUTOR_ADDRESS : undefined, Distributor.abi) as Contract | null
}

const fetchCampaigns = async (pairAddress: string): Promise<Campaign[]> => {
  try {
    const response = await axios.get(`https://api.merkl.xyz/v4/campaigns/`, {
      params: {
        chainId: CURRENT_CHAIN,
        mainParameter: pairAddress,
      },
    })
    return response?.data
  } catch (error) {
    console.error('Error fetching campaigns:', error)
    throw error
  }
}

const fetchOpportunity = async (opportunityId: string): Promise<any> => {
  try {
    const response = await axios.get(`https://api.merkl.xyz/v4/opportunities/${opportunityId}`)

    return response?.data
  } catch (error) {
    console.error('Error fetching opportunity:', error)
    throw error
  }
}

const fetchAllCampaigns = async (): Promise<Campaign[]> => {
  try {
    const response = await axios.get(`https://api.merkl.xyz/v4/campaigns/`, {
      params: {
        chainId: CURRENT_CHAIN,
      },
    })
    return response?.data
  } catch (error) {
    console.error('Error fetching campaigns:', error)
    throw error
  }
}

const fetchRewards = async (address: string): Promise<RewardData[]> => {
  try {
    const { data } = await axios.get(`https://api.merkl.xyz/v4/users/${address}/rewards`, {
      params: {
        chainId: CURRENT_CHAIN,
      },
    })
    return data
  } catch (error) {
    console.error('Error fetching rewards:', error)
    throw error
  }
}

export const useCampaigns = (pairAddress: string) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!pairAddress) return

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await fetchCampaigns(pairAddress)
        let campaigns = data

        campaigns.forEach(async (campaign, index) => {
          const opportnityId = campaign.opportunityId

          const opportunity = await fetchOpportunity(opportnityId)

          campaigns[index].opportunity = opportunity
        })

        setCampaigns(campaigns)
      } catch (err) {
        setError(err as Error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [pairAddress])

  const activeCampaigns = campaigns.filter((campaign) => {
    const now = Date.now()
    const start = Number(campaign.startTimestamp ? (campaign.startTimestamp as number) * 1000 : 0)
    const end = Number(campaign.endTimestamp ? (campaign.endTimestamp as number) * 1000 : 0)
    return now >= start && now <= end
  })

  return { campaigns: activeCampaigns, isLoading, error }
}

export const useAllCampaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await fetchAllCampaigns()

        setCampaigns(data)
      } catch (err) {
        setError(err as Error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const activeCampaigns = campaigns.filter((campaign) => {
    const now = Date.now()
    const start = Number(campaign.startTimestamp ? (campaign.startTimestamp as number) * 1000 : 0)
    const end = Number(campaign.endTimestamp ? (campaign.endTimestamp as number) * 1000 : 0)
    return now >= start && now <= end
  })

  return { campaigns: activeCampaigns, isLoading, error }
}

export const useRewards = () => {
  const { account } = useActiveWeb3React()
  const [rewards, setRewards] = useState<RewardData | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!account) return

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await fetchRewards(account)

        // Filter rewards for the current chain
        const filteredRewards = data.find((reward: any) => reward.chain.id === CURRENT_CHAIN)
        setRewards(filteredRewards ? filteredRewards : undefined)
      } catch (err) {
        setError(err as Error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [account])

  return {
    rewards,
    isLoading,
    error,
  }
}
